import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AIBrainVisualizer - A futuristic neural AI brain interface with logo core
 * 
 * Inspired by sci-fi AI systems like JARVIS, this creates the feeling
 * of interacting with a living AI intelligence system.
 * 
 * Features:
 * - Central hexagram logo as the AI core
 * - Neural network with animated nodes and connections
 * - Reactive sound waves from microphone input
 * - Multiple orbital rings representing neural layers
 * - Particle system for ambient effects
 * - State-based animations: idle, listening, thinking, speaking
 */

// AIRA ECHO Logo URL
const LOGO_URL = '/aira-echo-logo.png';

const AIBrainVisualizer = forwardRef(({
  mode = 'demo',
  size = 'lg',
  state: externalState,
  isActive = true,
  onStateChange,
  showLabel = false,
  className = '',
  tagline = 'Your AI Brain For Everything'
}, ref) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const streamRef = useRef(null);
  const logoRef = useRef(null);
  const logoLoadedRef = useRef(false);
  
  const [internalState, setInternalState] = useState('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  
  const currentState = externalState || internalState;

  // Size configurations
  // Reduced particle counts for better performance
  const sizes = {
    sm: { canvas: 200, coreRadius: 30, rings: 2, nodes: 8, particles: 15, logoSize: 40 },
    md: { canvas: 300, coreRadius: 42, rings: 3, nodes: 12, particles: 25, logoSize: 56 },
    lg: { canvas: 400, coreRadius: 55, rings: 3, nodes: 16, particles: 35, logoSize: 72 },
    xl: { canvas: 500, coreRadius: 68, rings: 4, nodes: 20, particles: 45, logoSize: 90 }
  };

  const config = sizes[size] || sizes.lg;

  // Color theme - Electric Blue & Dark Purple
  const colors = {
    primary: { r: 46, g: 92, b: 255 },      // #2E5CFF - Electric Blue
    secondary: { r: 112, g: 0, b: 255 },    // #7000FF - Dark Purple
    accent: { r: 0, g: 240, b: 255 },       // #00F0FF - Cyan
    core: { r: 100, g: 180, b: 255 },       // Light blue core
    node: { r: 150, g: 100, b: 255 },       // Purple nodes
  };

  // State-based configurations
  const stateConfig = {
    idle: { 
      intensity: 0.7, 
      pulseSpeed: 0.012, 
      waveSpeed: 0.5, 
      particleSpeed: 0.3,
      logoGlow: 0.4,
      logoPulse: 0.08
    },
    listening: { 
      intensity: 1.0, 
      pulseSpeed: 0.025, 
      waveSpeed: 1.5, 
      particleSpeed: 0.8,
      logoGlow: 0.8,
      logoPulse: 0.15
    },
    thinking: { 
      intensity: 0.95, 
      pulseSpeed: 0.04, 
      waveSpeed: 2.0, 
      particleSpeed: 1.2,
      logoGlow: 0.7,
      logoPulse: 0.2
    },
    speaking: { 
      intensity: 1.0, 
      pulseSpeed: 0.035, 
      waveSpeed: 1.8, 
      particleSpeed: 1.0,
      logoGlow: 0.9,
      logoPulse: 0.18
    }
  };

  // Load logo image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      logoRef.current = img;
      logoLoadedRef.current = true;
    };
    img.src = LOGO_URL;
  }, []);

  const updateState = useCallback((newState) => {
    setInternalState(newState);
    onStateChange?.(newState);
  }, [onStateChange]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    startListening: () => startMicrophone(),
    stopListening: () => stopMicrophone(),
    setState: (state) => updateState(state),
    getState: () => currentState,
    getAudioLevel: () => audioLevel
  }));

  // Start microphone
  const startMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      
      microphoneRef.current.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      updateState('listening');
      return true;
    } catch (error) {
      console.error('Microphone access denied:', error);
      return false;
    }
  }, [updateState]);

  // Stop microphone
  const stopMicrophone = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    microphoneRef.current = null;
    updateState('idle');
  }, [updateState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMicrophone();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [stopMicrophone]);

  // Main animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Animation state
    let time = 0;
    let particles = [];
    let nodes = [];
    let energyWaves = [];
    
    // Initialize particles
    for (let i = 0; i < config.particles; i++) {
      particles.push({
        angle: Math.random() * Math.PI * 2,
        radius: config.coreRadius + 25 + Math.random() * (config.canvas / 2 - config.coreRadius - 50),
        speed: 0.001 + Math.random() * 0.002,
        size: 1 + Math.random() * 2.5,
        opacity: 0.3 + Math.random() * 0.5,
        orbitOffset: Math.random() * Math.PI * 2
      });
    }
    
    // Initialize neural nodes
    for (let ring = 0; ring < config.rings; ring++) {
      const ringRadius = config.coreRadius + 35 + ring * ((config.canvas / 2 - config.coreRadius - 55) / config.rings);
      const nodeCount = Math.floor(config.nodes / config.rings) + ring * 2;
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          ring,
          angle: (i / nodeCount) * Math.PI * 2,
          radius: ringRadius,
          baseRadius: ringRadius,
          size: 2.5 + Math.random() * 3,
          pulseOffset: Math.random() * Math.PI * 2,
          connections: []
        });
      }
    }
    
    // Create connections between nodes
    nodes.forEach((node, idx) => {
      nodes.forEach((otherNode, otherIdx) => {
        if (idx !== otherIdx) {
          const dist = Math.abs(node.ring - otherNode.ring);
          const angleDiff = Math.abs(node.angle - otherNode.angle);
          if (dist <= 1 && angleDiff < Math.PI / 2 && Math.random() > 0.5) {
            node.connections.push(otherIdx);
          }
        }
      });
    });
    
    const draw = () => {
      const stateConf = stateConfig[currentState] || stateConfig.idle;
      
      // Get frequency data if available
      let dataArray = new Uint8Array(128);
      let averageLevel = 0;
      
      if (analyserRef.current && (currentState === 'listening' || currentState === 'speaking')) {
        analyserRef.current.getByteFrequencyData(dataArray);
        averageLevel = dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 255;
      } else {
        // Generate simulated data for demo/idle states
        for (let i = 0; i < 128; i++) {
          if (currentState === 'thinking') {
            dataArray[i] = Math.sin(time * 5 + i * 0.3) * 80 + 60 + Math.random() * 40;
          } else if (currentState === 'speaking') {
            dataArray[i] = Math.sin(time * 3 + i * 0.2) * 100 + 80 + Math.random() * 30;
          } else {
            dataArray[i] = Math.sin(time * 2 + i * 0.1) * 30 + 30;
          }
        }
        averageLevel = currentState === 'thinking' ? 0.5 : currentState === 'speaking' ? 0.7 : 0.2;
      }
      
      setAudioLevel(averageLevel);
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // ========== BACKGROUND GLOW ==========
      const bgGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, config.canvas / 2);
      bgGradient.addColorStop(0, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.15 * stateConf.intensity})`);
      bgGradient.addColorStop(0.5, `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${0.08 * stateConf.intensity})`);
      bgGradient.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // ========== ENERGY WAVES FROM LOGO (when speaking/listening) ==========
      if (currentState === 'listening' || currentState === 'speaking') {
        const waveCount = 6;
        for (let w = 0; w < waveCount; w++) {
          const waveProgress = ((time * stateConf.waveSpeed * 0.8 + w / waveCount) % 1);
          const waveRadius = config.coreRadius * 0.8 + waveProgress * (config.canvas / 2 - config.coreRadius);
          const waveOpacity = (1 - waveProgress) * 0.5 * averageLevel * stateConf.intensity;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, waveRadius, 0, Math.PI * 2);
          ctx.strokeStyle = currentState === 'speaking'
            ? `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${waveOpacity})`
            : `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${waveOpacity})`;
          ctx.lineWidth = 2 + averageLevel * 4;
          ctx.stroke();
        }
      }
      
      // ========== ORBITAL RINGS ==========
      for (let ring = 0; ring < config.rings; ring++) {
        const ringRadius = config.coreRadius + 35 + ring * ((config.canvas / 2 - config.coreRadius - 55) / config.rings);
        const ringOpacity = 0.08 + (ring / config.rings) * 0.12;
        const pulseOffset = Math.sin(time * stateConf.pulseSpeed * 60 + ring) * 5 * averageLevel;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius + pulseOffset, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${ringOpacity * stateConf.intensity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Dashed accent ring
        ctx.setLineDash([4, 12]);
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius + pulseOffset - 4, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${ringOpacity * 0.4 * stateConf.intensity})`;
        ctx.stroke();
        ctx.setLineDash([]);
      }
      
      // ========== NEURAL CONNECTIONS ==========
      nodes.forEach((node, idx) => {
        const nodeX = centerX + Math.cos(node.angle + time * stateConf.waveSpeed * 0.08) * (node.radius + averageLevel * 12);
        const nodeY = centerY + Math.sin(node.angle + time * stateConf.waveSpeed * 0.08) * (node.radius + averageLevel * 12);
        
        node.connections.forEach(connIdx => {
          const connNode = nodes[connIdx];
          const connX = centerX + Math.cos(connNode.angle + time * stateConf.waveSpeed * 0.08) * (connNode.radius + averageLevel * 12);
          const connY = centerY + Math.sin(connNode.angle + time * stateConf.waveSpeed * 0.08) * (connNode.radius + averageLevel * 12);
          
          const connectionGradient = ctx.createLinearGradient(nodeX, nodeY, connX, connY);
          const connOpacity = 0.12 + averageLevel * 0.25;
          connectionGradient.addColorStop(0, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${connOpacity})`);
          connectionGradient.addColorStop(1, `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${connOpacity})`);
          
          ctx.beginPath();
          ctx.moveTo(nodeX, nodeY);
          ctx.lineTo(connX, connY);
          ctx.strokeStyle = connectionGradient;
          ctx.lineWidth = 0.5 + averageLevel * 0.8;
          ctx.stroke();
          
          // Animated energy pulse along connection
          if (currentState === 'thinking' || currentState === 'speaking' || currentState === 'listening') {
            const pulsePos = (Math.sin(time * 4 + idx * 0.3) + 1) / 2;
            const pulseX = nodeX + (connX - nodeX) * pulsePos;
            const pulseY = nodeY + (connY - nodeY) * pulsePos;
            
            ctx.beginPath();
            ctx.arc(pulseX, pulseY, 1.5 + averageLevel, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.7 * stateConf.intensity})`;
            ctx.fill();
          }
        });
      });
      
      // ========== NEURAL NODES ==========
      nodes.forEach((node) => {
        const nodePulse = Math.sin(time * 2.5 + node.pulseOffset) * 0.4 + 1;
        const nodeX = centerX + Math.cos(node.angle + time * stateConf.waveSpeed * 0.08) * (node.radius + averageLevel * 12);
        const nodeY = centerY + Math.sin(node.angle + time * stateConf.waveSpeed * 0.08) * (node.radius + averageLevel * 12);
        
        // Node glow
        const glowGradient = ctx.createRadialGradient(nodeX, nodeY, 0, nodeX, nodeY, node.size * 3 * nodePulse);
        glowGradient.addColorStop(0, `rgba(${colors.node.r}, ${colors.node.g}, ${colors.node.b}, ${0.5 * stateConf.intensity})`);
        glowGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGradient;
        ctx.fillRect(nodeX - node.size * 4, nodeY - node.size * 4, node.size * 8, node.size * 8);
        
        // Node core
        ctx.beginPath();
        ctx.arc(nodeX, nodeY, node.size * nodePulse * (0.7 + averageLevel * 0.4), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.75 * stateConf.intensity})`;
        ctx.fill();
        
        // Node highlight
        ctx.beginPath();
        ctx.arc(nodeX, nodeY, node.size * 0.4 * nodePulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.7 * stateConf.intensity})`;
        ctx.fill();
      });
      
      // ========== FREQUENCY BARS (Neural Signals from Logo) ==========
      const barCount = 48;
      for (let i = 0; i < barCount; i++) {
        const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2;
        const dataIndex = Math.floor(i * dataArray.length / barCount);
        const value = dataArray[dataIndex] / 255;
        
        const innerRadius = config.coreRadius + 8;
        const barHeight = 8 + value * 35 * stateConf.intensity;
        const outerRadius = innerRadius + barHeight;
        
        const x1 = centerX + Math.cos(angle) * innerRadius;
        const y1 = centerY + Math.sin(angle) * innerRadius;
        const x2 = centerX + Math.cos(angle) * outerRadius;
        const y2 = centerY + Math.sin(angle) * outerRadius;
        
        // Color interpolation
        const colorProgress = i / barCount;
        const r = Math.round(colors.primary.r + (colors.accent.r - colors.primary.r) * colorProgress);
        const g = Math.round(colors.primary.g + (colors.accent.g - colors.primary.g) * colorProgress);
        const b = Math.round(colors.primary.b + (colors.accent.b - colors.primary.b) * colorProgress);
        
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.25 + value * 0.55})`;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
      }
      
      // ========== AI CORE - LOGO BACKGROUND GLOW ==========
      const logoPulse = 1 + Math.sin(time * 2.5) * stateConf.logoPulse + averageLevel * 0.15;
      
      // Outer glow layers
      for (let g = 3; g >= 0; g--) {
        const glowSize = config.coreRadius * (1.3 + g * 0.25) * logoPulse;
        const glowGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, glowSize);
        
        if (currentState === 'speaking') {
          glowGradient.addColorStop(0, `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.3 * stateConf.logoGlow / (g + 1)})`);
          glowGradient.addColorStop(0.5, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.15 * stateConf.logoGlow / (g + 1)})`);
        } else if (currentState === 'listening') {
          glowGradient.addColorStop(0, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.35 * stateConf.logoGlow / (g + 1)})`);
          glowGradient.addColorStop(0.5, `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.15 * stateConf.logoGlow / (g + 1)})`);
        } else if (currentState === 'thinking') {
          glowGradient.addColorStop(0, `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${0.3 * stateConf.logoGlow / (g + 1)})`);
          glowGradient.addColorStop(0.5, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.15 * stateConf.logoGlow / (g + 1)})`);
        } else {
          glowGradient.addColorStop(0, `rgba(${colors.core.r}, ${colors.core.g}, ${colors.core.b}, ${0.25 * stateConf.logoGlow / (g + 1)})`);
          glowGradient.addColorStop(0.5, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.1 * stateConf.logoGlow / (g + 1)})`);
        }
        glowGradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, glowSize, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();
      }
      
      // ========== PULSING RING AROUND LOGO ==========
      ctx.beginPath();
      ctx.arc(centerX, centerY, config.coreRadius * logoPulse, 0, Math.PI * 2);
      const ringGradient = ctx.createLinearGradient(
        centerX - config.coreRadius, centerY,
        centerX + config.coreRadius, centerY
      );
      
      if (currentState === 'speaking') {
        ringGradient.addColorStop(0, `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.9 * stateConf.intensity})`);
        ringGradient.addColorStop(0.5, `rgba(255, 255, 255, ${0.4 * stateConf.intensity})`);
        ringGradient.addColorStop(1, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.9 * stateConf.intensity})`);
      } else if (currentState === 'listening') {
        ringGradient.addColorStop(0, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.9 * stateConf.intensity})`);
        ringGradient.addColorStop(0.5, `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.6 * stateConf.intensity})`);
        ringGradient.addColorStop(1, `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${0.9 * stateConf.intensity})`);
      } else {
        ringGradient.addColorStop(0, `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, ${0.7 * stateConf.intensity})`);
        ringGradient.addColorStop(0.5, `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${0.5 * stateConf.intensity})`);
        ringGradient.addColorStop(1, `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${0.7 * stateConf.intensity})`);
      }
      
      ctx.strokeStyle = ringGradient;
      ctx.lineWidth = 2 + averageLevel * 2;
      ctx.stroke();
      
      // Inner dark circle for logo background
      ctx.beginPath();
      ctx.arc(centerX, centerY, config.coreRadius * 0.9 * logoPulse, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(3, 0, 20, 0.85)';
      ctx.fill();
      
      // ========== DRAW LOGO IN CENTER ==========
      if (logoLoadedRef.current && logoRef.current) {
        const logoSize = config.logoSize * logoPulse;
        const logoX = centerX - logoSize / 2;
        const logoY = centerY - logoSize / 2;
        
        // Add glow effect to logo
        ctx.save();
        ctx.globalAlpha = stateConf.intensity;
        
        // Shadow glow
        ctx.shadowBlur = 15 + averageLevel * 20;
        if (currentState === 'speaking') {
          ctx.shadowColor = `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, 0.8)`;
        } else if (currentState === 'listening') {
          ctx.shadowColor = `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, 0.8)`;
        } else if (currentState === 'thinking') {
          ctx.shadowColor = `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, 0.8)`;
        } else {
          ctx.shadowColor = `rgba(${colors.primary.r}, ${colors.primary.g}, ${colors.primary.b}, 0.5)`;
        }
        
        ctx.drawImage(logoRef.current, logoX, logoY, logoSize, logoSize);
        ctx.restore();
      }
      
      // ========== FLOATING PARTICLES ==========
      particles.forEach(particle => {
        particle.angle += particle.speed * stateConf.particleSpeed;
        const particleRadius = particle.radius + Math.sin(time + particle.orbitOffset) * 10;
        const px = centerX + Math.cos(particle.angle) * particleRadius;
        const py = centerY + Math.sin(particle.angle) * particleRadius;
        
        ctx.beginPath();
        ctx.arc(px, py, particle.size * (0.8 + averageLevel * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${particle.opacity * stateConf.intensity})`;
        ctx.fill();
      });
      
      // ========== DATA STREAMS (for thinking state) ==========
      if (currentState === 'thinking') {
        for (let i = 0; i < 8; i++) {
          const streamAngle = (i / 8) * Math.PI * 2 + time * 1.5;
          const streamLength = 25 + Math.sin(time * 3 + i) * 15;
          
          for (let j = 0; j < streamLength; j++) {
            const progress = j / streamLength;
            const streamRadius = config.coreRadius + 15 + progress * 50;
            const sx = centerX + Math.cos(streamAngle + progress * 0.4) * streamRadius;
            const sy = centerY + Math.sin(streamAngle + progress * 0.4) * streamRadius;
            
            ctx.beginPath();
            ctx.arc(sx, sy, 2 * (1 - progress), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${colors.secondary.r}, ${colors.secondary.g}, ${colors.secondary.b}, ${(1 - progress) * 0.7})`;
            ctx.fill();
          }
        }
      }
      
      // ========== SPEAKING RIPPLES ==========
      if (currentState === 'speaking') {
        for (let i = 0; i < 4; i++) {
          const rippleProgress = ((time * 2 + i * 0.25) % 1);
          const rippleRadius = config.coreRadius * (1 + rippleProgress * 0.5);
          const rippleOpacity = (1 - rippleProgress) * 0.4 * stateConf.intensity;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, rippleRadius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${colors.accent.r}, ${colors.accent.g}, ${colors.accent.b}, ${rippleOpacity})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
      
      time += stateConf.pulseSpeed;
      
      if (isActive) {
        animationRef.current = requestAnimationFrame(draw);
      }
    };
    
    if (isActive) {
      draw();
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, currentState, config]);

  // State indicator labels
  const stateLabels = {
    idle: 'Tap to speak',
    listening: 'Listening...',
    thinking: 'Processing...',
    speaking: 'AIRA is speaking'
  };

  const stateDescriptions = {
    idle: 'Click the AI brain to start a conversation',
    listening: 'Speak naturally, I\'m listening to you',
    thinking: 'Processing your request...',
    speaking: 'Playing AI response'
  };

  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      {/* Ambient background glow */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: config.canvas + 150,
          height: config.canvas + 150,
          background: currentState === 'listening' 
            ? 'radial-gradient(circle, rgba(46, 92, 255, 0.25) 0%, rgba(0, 240, 255, 0.1) 40%, transparent 70%)'
            : currentState === 'speaking'
            ? 'radial-gradient(circle, rgba(0, 240, 255, 0.3) 0%, rgba(112, 0, 255, 0.1) 40%, transparent 70%)'
            : currentState === 'thinking'
            ? 'radial-gradient(circle, rgba(112, 0, 255, 0.25) 0%, rgba(46, 92, 255, 0.1) 40%, transparent 70%)'
            : 'radial-gradient(circle, rgba(46, 92, 255, 0.15) 0%, rgba(112, 0, 255, 0.05) 40%, transparent 70%)'
        }}
        animate={{
          scale: currentState === 'idle' ? [1, 1.05, 1] : [1, 1.12, 1],
          opacity: currentState === 'idle' ? [0.5, 0.7, 0.5] : [0.7, 1, 0.7]
        }}
        transition={{
          duration: currentState === 'idle' ? 4 : 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
      
      {/* Main canvas */}
      <canvas
        ref={canvasRef}
        width={config.canvas}
        height={config.canvas}
        className="relative z-10"
        style={{ width: config.canvas, height: config.canvas }}
      />
      
      {/* State indicator */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentState}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-20 text-center"
          style={{ 
            bottom: size === 'sm' ? '-50px' : size === 'md' ? '-60px' : '-70px'
          }}
        >
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              {currentState === 'listening' && (
                <motion.span 
                  className="w-2 h-2 bg-primary rounded-full"
                  animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                />
              )}
              {currentState === 'thinking' && (
                <motion.span 
                  className="w-2 h-2 bg-secondary rounded-full"
                  animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
              )}
              {currentState === 'speaking' && (
                <motion.span 
                  className="w-2 h-2 bg-accent rounded-full"
                  animate={{ scale: [1, 2, 1] }}
                  transition={{ duration: 0.3, repeat: Infinity }}
                />
              )}
              <span 
                className={`font-medium ${
                  currentState === 'listening' ? 'text-primary' :
                  currentState === 'speaking' ? 'text-accent' :
                  currentState === 'thinking' ? 'text-secondary' :
                  'text-gray-400'
                }`}
                style={{ fontSize: size === 'sm' ? '12px' : '14px' }}
              >
                {stateLabels[currentState]}
              </span>
            </div>
            <span 
              className="text-gray-500"
              style={{ fontSize: size === 'sm' ? '10px' : '12px' }}
            >
              {stateDescriptions[currentState]}
            </span>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
});

AIBrainVisualizer.displayName = 'AIBrainVisualizer';

export { AIBrainVisualizer };
